/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import * as incidentsApi from "../api/incidents";

const TABS = {
  INCIDENT_REVIEW: "incident_review",
  CORRECTIVE_ACTIONS: "corrective_actions",
};

const CORRECTIVE_ACTION_FILTERS = [
  { value: "all", label: "All active incidents" },
  { value: "overdue", label: "Overdue actions" },
  { value: "awaiting_verification", label: "Awaiting verification" },
  { value: "blocked", label: "Blocked" },
  { value: "active", label: "Active actions" },
  { value: "complete", label: "All actions complete" },
  { value: "verified", label: "Fully verified" },
];

export default function ReviewQueue() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TABS.INCIDENT_REVIEW);
  const [incidentReviews, setIncidentReviews] = useState([]);
  const [correctiveActionIncidents, setCorrectiveActionIncidents] = useState([]);
  const [loadingIncidentReviews, setLoadingIncidentReviews] = useState(true);
  const [loadingCorrectiveActions, setLoadingCorrectiveActions] = useState(false);
  const [correctiveActionsError, setCorrectiveActionsError] = useState("");
  const [correctiveActionsLoaded, setCorrectiveActionsLoaded] = useState(false);
  const [correctiveActionsApiAvailable, setCorrectiveActionsApiAvailable] =
    useState(true);
  const [correctiveActionFilter, setCorrectiveActionFilter] = useState("all");

  useEffect(() => {
    loadIncidentReviews();
  }, []);

  useEffect(() => {
    if (
      activeTab === TABS.CORRECTIVE_ACTIONS &&
      !correctiveActionsLoaded &&
      !loadingCorrectiveActions
    ) {
      loadCorrectiveActionIncidents();
    }
  }, [activeTab, correctiveActionsLoaded, loadingCorrectiveActions]);

  async function loadIncidentReviews() {
    try {
      setLoadingIncidentReviews(true);
      const data = await incidentsApi.getReviewQueue();
      setIncidentReviews(normalizeListResponse(data));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load incidents pending review");
    } finally {
      setLoadingIncidentReviews(false);
    }
  }

  async function loadCorrectiveActionIncidents() {
    const loader = getCorrectiveActionQueueLoader();

    if (!loader) {
      setCorrectiveActionsApiAvailable(false);
      setCorrectiveActionsLoaded(true);
      setCorrectiveActionIncidents([]);
      return;
    }

    try {
      setLoadingCorrectiveActions(true);
      setCorrectiveActionsError("");
      setCorrectiveActionsApiAvailable(true);
      const data = await loader();
      setCorrectiveActionIncidents(normalizeListResponse(data));
      setCorrectiveActionsLoaded(true);
    } catch (err) {
      console.error(err);
      setCorrectiveActionsError("Unable to load corrective actions. Use Refresh to try again.");
      toast.error("Failed to load corrective action oversight");
    } finally {
      setCorrectiveActionsLoaded(true);
      setLoadingCorrectiveActions(false);
    }
  }

  function getCorrectiveActionQueueLoader() {
    return (
      incidentsApi.getCorrectiveActionOversightQueue
    );
  }

  function refreshActiveTab() {
    if (activeTab === TABS.INCIDENT_REVIEW) {
      loadIncidentReviews();
      return;
    }

    loadCorrectiveActionIncidents();
  }

  const filteredCorrectiveActionIncidents = useMemo(() => {
    return correctiveActionIncidents.filter((item) =>
      matchesCorrectiveActionFilter(item, correctiveActionFilter),
    );
  }, [correctiveActionIncidents, correctiveActionFilter]);

  const oversightCounts = useMemo(() => {
    return correctiveActionIncidents.reduce(
      (counts, item) => {
        const summary = getCorrectiveActionSummary(item);

        if (summary.overdue > 0) counts.overdueIncidents += 1;
        if (summary.awaitingVerification > 0) {
          counts.awaitingVerificationIncidents += 1;
        }
        if (summary.blocked > 0) counts.blockedIncidents += 1;
        counts.openActions += summary.active;

        return counts;
      },
      {
        overdueIncidents: 0,
        awaitingVerificationIncidents: 0,
        blockedIncidents: 0,
        openActions: 0,
      },
    );
  }, [correctiveActionIncidents]);

  const loading =
    activeTab === TABS.INCIDENT_REVIEW
      ? loadingIncidentReviews
      : loadingCorrectiveActions;

  return (
    <div className="px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Review Queue
            </h1>
            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              Incident review + action oversight
            </span>
          </div>
          <p className="text-slate-500 mt-1">
            Review new incidents, then monitor and verify the corrective actions
            created from finalized NCRs.
          </p>
        </div>

        <button
          type="button"
          onClick={refreshActiveTab}
          disabled={loading}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="border-b border-slate-200 dark:border-slate-800">
        <nav className="flex gap-6" aria-label="Review queue sections">
          <TabButton
            active={activeTab === TABS.INCIDENT_REVIEW}
            count={incidentReviews.length}
            onClick={() => setActiveTab(TABS.INCIDENT_REVIEW)}
          >
            Incidents Pending Review
          </TabButton>

          <TabButton
            active={activeTab === TABS.CORRECTIVE_ACTIONS}
            count={
              correctiveActionsLoaded ? correctiveActionIncidents.length : null
            }
            onClick={() => setActiveTab(TABS.CORRECTIVE_ACTIONS)}
          >
            Corrective Action Oversight
          </TabButton>
        </nav>
      </div>

      {activeTab === TABS.INCIDENT_REVIEW ? (
        <IncidentReviewTab
          incidents={incidentReviews}
          loading={loadingIncidentReviews}
          onOpenIncident={(incidentId) =>
            navigate(`/secure/incidents/${incidentId}`)
          }
        />
      ) : (
        <CorrectiveActionOversightTab
          error={correctiveActionsError}
          incidents={correctiveActionIncidents}
          filteredIncidents={filteredCorrectiveActionIncidents}
          loading={loadingCorrectiveActions}
          apiAvailable={correctiveActionsApiAvailable}
          filter={correctiveActionFilter}
          onFilterChange={setCorrectiveActionFilter}
          counts={oversightCounts}
          onOpenIncident={(incidentId) =>
            navigate(`/secure/incidents/${incidentId}`)
          }
        />
      )}
    </div>
  );
}

function TabButton({ active, count, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative pb-3 text-sm font-semibold transition-colors ${
        active
          ? "text-primary"
          : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {children}
        {count !== null && count !== undefined && (
          <span
            className={`inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-xs ${
              active
                ? "bg-primary/10 text-primary"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            {count}
          </span>
        )}
      </span>

      {active && (
        <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
      )}
    </button>
  );
}

function IncidentReviewTab({ incidents, loading, onOpenIncident }) {
  if (loading) {
    return <LoadingState message="Loading incidents pending review..." />;
  }

  if (incidents.length === 0) {
    return (
      <EmptyState
        title="No incidents are waiting for review"
        message="Newly processed incidents will appear here when reviewer action is required."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <QueueMetric
          label="Incidents requiring review"
          value={incidents.length}
          tone="purple"
        />
        <QueueMetric
          label="Oldest waiting"
          value={formatAge(getOldestDate(incidents))}
          tone="slate"
        />
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left p-4">Incident</th>
              <th className="text-left p-4">Title</th>
              <th className="text-left p-4">Review status</th>
              <th className="text-left p-4">Submitted</th>
              <th className="text-left p-4">Last updated</th>
              <th className="text-right p-4">Action</th>
            </tr>
          </thead>

          <tbody>
            {incidents.map((item) => (
              <tr
                key={item.id}
                className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
              >
                <td className="p-4 align-top">
                  <IncidentLink item={item} onOpen={onOpenIncident} />
                </td>

                <td className="p-4 align-top text-slate-600 dark:text-slate-300">
                  <div className="max-w-sm">
                    {item.title || "Untitled Incident"}
                  </div>
                </td>

                <td className="p-4 align-top">
                  <IncidentStatusBadge item={item} />
                </td>

                <td className="p-4 align-top text-slate-500 whitespace-nowrap">
                  {formatDate(item.submitted_at || item.created_at)}
                </td>

                <td className="p-4 align-top text-slate-500 whitespace-nowrap">
                  {formatDate(item.updated_at)}
                </td>

                <td className="p-4 align-top text-right">
                  <PrimaryActionButton
                    label="Review"
                    onClick={() => onOpenIncident(item.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CorrectiveActionOversightTab({
  error,
  incidents,
  filteredIncidents,
  loading,
  apiAvailable,
  filter,
  onFilterChange,
  counts,
  onOpenIncident,
}) {
  if (loading) {
    return <LoadingState message="Loading corrective action oversight..." />;
  }

  if (error) return <div role="alert">{error}</div>;

  if (!apiAvailable) {
    return (
      <EmptyState
        title="Corrective action queue API is not connected yet"
        message="This tab needs a post-finalization incident endpoint. Add getCorrectiveActionReviewQueue, getCorrectiveActionOversightQueue, or getIncidentOversightQueue to api/incidents.js."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <QueueMetric
          label="Open corrective actions"
          value={counts.openActions}
          tone="blue"
        />
        <QueueMetric
          label="Incidents with overdue actions"
          value={counts.overdueIncidents}
          tone="red"
        />
        <QueueMetric
          label="Awaiting verification"
          value={counts.awaitingVerificationIncidents}
          tone="amber"
        />
        <QueueMetric
          label="Incidents with blocked actions"
          value={counts.blockedIncidents}
          tone="slate"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {CORRECTIVE_ACTION_FILTERS.map((option) => {
          const selected = filter === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onFilterChange(option.value)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                selected
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {incidents.length === 0 && (
        <EmptyState
          title="No finalized incidents have corrective actions"
          message="Incidents will appear here after an NCR is finalized and corrective actions are created."
        />
      )}

      {incidents.length > 0 && filteredIncidents.length === 0 && (
        <div className="bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-12 text-center">
          <p className="font-semibold text-slate-700 dark:text-slate-200">
            No incidents match this corrective action filter.
          </p>
          <button
            type="button"
            onClick={() => onFilterChange("all")}
            className="mt-3 text-sm font-semibold text-primary"
          >
            Show all active incidents
          </button>
        </div>
      )}

      {filteredIncidents.length > 0 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[1080px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
              <tr>
                <th className="text-left p-4">Incident</th>
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4">Incident status</th>
                <th className="text-left p-4">Corrective actions</th>
                <th className="text-left p-4">Last updated</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredIncidents.map((item) => {
                const summary = getCorrectiveActionSummary(item);

                return (
                  <tr
                    key={item.id}
                    className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                  >
                    <td className="p-4 align-top">
                      <IncidentLink item={item} onOpen={onOpenIncident} />
                    </td>

                    <td className="p-4 align-top text-slate-600 dark:text-slate-300">
                      <div className="max-w-sm">
                        {item.title || "Untitled Incident"}
                      </div>
                    </td>

                    <td className="p-4 align-top">
                      <IncidentStatusBadge item={item} />
                    </td>

                    <td className="p-4 align-top">
                      <CorrectiveActionSummary summary={summary} />
                    </td>

                    <td className="p-4 align-top text-slate-500 whitespace-nowrap">
                      {formatDate(item.updated_at)}
                    </td>

                    <td className="p-4 align-top text-right">
                      <PrimaryActionButton
                        label={
                          summary.awaitingVerification > 0
                            ? "Review actions"
                            : "Open incident"
                        }
                        onClick={() => onOpenIncident(item.id)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IncidentLink({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className="font-semibold text-left text-slate-900 dark:text-white hover:text-primary"
    >
      {item.incident_number || "Unnumbered incident"}
    </button>
  );
}

function PrimaryActionButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 rounded-lg bg-primary text-white font-semibold text-xs hover:opacity-90"
    >
      {label}
    </button>
  );
}

function LoadingState({ message }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center text-slate-500">
      {message}
    </div>
  );
}

function EmptyState({ title, message }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-12 text-center">
      <p className="font-semibold text-slate-700 dark:text-slate-200">
        {title}
      </p>
      <p className="mt-2 text-sm text-slate-500">{message}</p>
    </div>
  );
}

function QueueMetric({ label, value, tone }) {
  const toneClasses = {
    purple:
      "bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-950/30 dark:border-purple-900 dark:text-purple-200",
    blue: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/30 dark:border-blue-900 dark:text-blue-200",
    red: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200",
    amber:
      "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200",
    slate:
      "bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200",
  };

  return (
    <div className={`rounded-xl border p-4 ${toneClasses[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wide opacity-80">
        {label}
      </div>
    </div>
  );
}

function IncidentStatusBadge({ item }) {
  const awaitingReview = isIncidentAwaitingReview(item);
  const status = String(item.status || "").toLowerCase();

  if (awaitingReview) {
    return <StatusBadge tone="purple">Under review</StatusBadge>;
  }

  if (["closed", "verified", "complete", "completed"].includes(status)) {
    return <StatusBadge tone="green">{formatStatus(item.status)}</StatusBadge>;
  }

  if (["finalized", "active", "corrective_action"].includes(status)) {
    return <StatusBadge tone="blue">{formatStatus(item.status)}</StatusBadge>;
  }

  return <StatusBadge tone="slate">{formatStatus(item.status)}</StatusBadge>;
}

function CorrectiveActionSummary({ summary }) {
  if (summary.total === 0) {
    return <span className="text-slate-400">No actions created</span>;
  }

  return (
    <div className="space-y-2 min-w-[250px]">
      <div className="font-semibold text-slate-800 dark:text-slate-100">
        {summary.total} {summary.total === 1 ? "action" : "actions"}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {summary.active > 0 && (
          <StatusBadge tone="blue">{summary.active} active</StatusBadge>
        )}

        {summary.overdue > 0 && (
          <StatusBadge tone="red">{summary.overdue} overdue</StatusBadge>
        )}

        {summary.blocked > 0 && (
          <StatusBadge tone="slate">{summary.blocked} blocked</StatusBadge>
        )}

        {summary.awaitingVerification > 0 && (
          <StatusBadge tone="amber">
            {summary.awaitingVerification} awaiting verification
          </StatusBadge>
        )}

        {summary.verified > 0 && (
          <StatusBadge tone="green">{summary.verified} verified</StatusBadge>
        )}
      </div>

      {summary.assigneeNames.length > 0 && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Assigned to {summarizeAssignees(summary.assigneeNames)}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ children, tone = "slate" }) {
  const toneClasses = {
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    red: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    amber:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    slate:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${toneClasses[tone]}`}
    >
      {children}
    </span>
  );
}

function matchesCorrectiveActionFilter(item, filter) {
  if (filter === "all") return true;

  const summary = getCorrectiveActionSummary(item);

  switch (filter) {
    case "overdue":
      return summary.overdue > 0;
    case "awaiting_verification":
      return summary.awaitingVerification > 0;
    case "blocked":
      return summary.blocked > 0;
    case "active":
      return summary.active > 0;
    case "complete":
      return (
        summary.total > 0 &&
        summary.active === 0 &&
        summary.awaitingVerification > 0
      );
    case "verified":
      return summary.total > 0 && summary.verified >= summary.total;
    default:
      return true;
  }
}

function isIncidentAwaitingReview(item) {
  const status = String(item.status || "").toLowerCase();

  return (
    item.awaiting_review === true ||
    item.requires_review === true ||
    [
      "under_review",
      "pending_review",
      "review_pending",
      "processed",
      "ready_for_review",
    ].includes(status)
  );
}

function getCorrectiveActionSummary(item) {
  const source =
    item.corrective_action_summary ||
    item.work_item_summary ||
    item.work_items_summary ||
    {};

  const workItems = Array.isArray(item.work_items)
    ? item.work_items
    : Array.isArray(item.corrective_actions)
      ? item.corrective_actions
      : [];

  const total = getNumber(
    source.total,
    source.total_count,
    source.count,
    item.work_item_count,
    item.corrective_action_count,
    workItems.length,
  );

  const overdue = getNumber(
    source.overdue,
    source.overdue_count,
    item.overdue_work_item_count,
    countItems(workItems, isOverdue),
  );

  const awaitingVerification = getNumber(
    source.awaiting_verification,
    source.awaiting_verification_count,
    source.pending_verification,
    source.pending_verification_count,
    item.awaiting_verification_count,
    countItems(workItems, isAwaitingVerification),
  );

  const blocked = getNumber(
    source.blocked,
    source.blocked_count,
    item.blocked_work_item_count,
    countItems(workItems, isBlocked),
  );

  const verified = getNumber(
    source.verified,
    source.verified_count,
    item.verified_work_item_count,
    countItems(workItems, isVerified),
  );

  const completed = getNumber(
    source.completed,
    source.completed_count,
    item.completed_work_item_count,
    countItems(workItems, isCompleted),
  );

  const explicitActive = firstFiniteNumber(
    source.active,
    source.active_count,
    source.open,
    source.open_count,
    item.active_work_item_count,
    item.open_work_item_count,
  );

  const active =
    explicitActive ?? Math.max(total - Math.max(completed, verified), 0);

  const assigneeNames = getAssigneeNames(source, workItems);

  return {
    total,
    active,
    overdue,
    blocked,
    awaitingVerification,
    completed,
    verified,
    assigneeNames,
  };
}

function getAssigneeNames(source, workItems) {
  const summaryNames =
    source.assignee_names || source.assignees || source.assigned_to_names;

  if (Array.isArray(summaryNames)) {
    return uniqueStrings(summaryNames);
  }

  return uniqueStrings(
    workItems.map(
      (workItem) =>
        workItem.assigned_to_name ||
        workItem.owner_name ||
        workItem.assignee_name,
    ),
  );
}

function countItems(items, predicate) {
  return items.reduce((count, item) => count + (predicate(item) ? 1 : 0), 0);
}

function isOverdue(workItem) {
  if (!workItem?.due_date || isCompleted(workItem) || isVerified(workItem)) {
    return false;
  }

  const dueDate = new Date(workItem.due_date);
  return !Number.isNaN(dueDate.getTime()) && dueDate < new Date();
}

function isAwaitingVerification(workItem) {
  const status = String(workItem?.status || "").toLowerCase();

  return (
    workItem?.awaiting_verification === true ||
    workItem?.verification_status === "pending" ||
    (["complete", "completed"].includes(status) && !workItem?.verified_at)
  );
}

function isBlocked(workItem) {
  return String(workItem?.status || "").toLowerCase() === "blocked";
}

function isCompleted(workItem) {
  return ["complete", "completed"].includes(
    String(workItem?.status || "").toLowerCase(),
  );
}

function isVerified(workItem) {
  return (
    Boolean(workItem?.verified_at) ||
    ["verified", "closed"].includes(
      String(workItem?.status || "").toLowerCase(),
    )
  );
}

function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.incidents)) return data.incidents;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getOldestDate(items) {
  const validDates = items
    .map((item) => item.submitted_at || item.created_at || item.updated_at)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (validDates.length === 0) return null;

  return new Date(Math.min(...validDates.map((date) => date.getTime())));
}

function formatAge(date) {
  if (!date) return "—";

  const elapsedMs = Date.now() - date.getTime();
  const elapsedDays = Math.max(Math.floor(elapsedMs / 86400000), 0);

  if (elapsedDays === 0) return "Today";
  if (elapsedDays === 1) return "1 day";
  return `${elapsedDays} days`;
}

function getNumber(...values) {
  return firstFiniteNumber(...values) ?? 0;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }

  return null;
}

function uniqueStrings(values) {
  return [...new Set(values.filter(Boolean).map(String))];
}

function summarizeAssignees(names) {
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString();
}

function formatStatus(status) {
  if (!status) return "Unknown";

  return String(status)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
